// Calendar service - Real API calls to backend
import { apiClient } from './api';
import { API_ENDPOINTS } from '../utils/constants';
import { formatJobTitle } from '../types/job';

// Get calendar events for a user based on their role (real API)
export const getCalendarEvents = async (userId, userRole) => {
  const events = [];
  
  if (userRole === 'employee') {
    // For employees: fetch job matches (potential matches, accepted jobs)
    try {
      const matches = await apiClient.get('/job-matches/');
      const matchesArray = Array.isArray(matches) ? matches : [];
      
      matchesArray.forEach(match => {
        const jobPosting = match.job_posting_detail || match.job_posting;
        if (!jobPosting || !jobPosting.date) return;
        
        const matchStatus = match.status || 'pending';
        let eventType, backgroundColor, borderColor;
        
        if (matchStatus === 'accepted') {
          eventType = 'accepted_job';
          backgroundColor = '#4169e1'; // Blue
          borderColor = '#4169e1';
        } else if (matchStatus === 'pending') {
          eventType = 'potential_match';
          backgroundColor = '#ffa500'; // Orange
          borderColor = '#ffa500';
        } else {
          return; // Skip rejected/discarded matches
        }
        
        const title = formatJobTitle(jobPosting, userRole, match);
        
        events.push({
          id: `match-${match.id}`,
          title: title,
          start: jobPosting.date,
          allDay: true,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          textColor: '#ffffff',
          display: 'block',
          extendedProps: {
            type: eventType,
            match_id: match.id,
            job_id: jobPosting.id,
            match: match
          }
        });
      });
    } catch (error) {
      console.error('Error fetching employee matches:', error);
    }
    
    // Fetch availability separately (handled in Dashboard component)
    
  } else if (userRole === 'employer') {
    // For employers: fetch their job postings
    try {
      const jobPostings = await apiClient.get(`/job-postings/?employer_id=${userId}`);
      const postingsArray = Array.isArray(jobPostings) ? jobPostings : [];
      
      postingsArray.forEach(job => {
        if (!job.date) return;
        
        const jobStatus = job.status || 'open';
        let eventType, backgroundColor, borderColor;
        
        if (jobStatus === 'accepted') {
          eventType = 'accepted_job';
          backgroundColor = '#4169e1'; // Blue
          borderColor = '#4169e1';
        } else if (jobStatus === 'match_found') {
          eventType = 'potential_match';
          backgroundColor = '#ffa500'; // Orange
          borderColor = '#ffa500';
        } else if (jobStatus === 'done' || jobStatus === 'completed') {
          eventType = 'completed_job';
          backgroundColor = '#9370db'; // Purple
          borderColor = '#9370db';
        } else {
          eventType = 'posted_job';
          backgroundColor = '#ff6347'; // Red
          borderColor = '#ff6347';
        }
        
        const title = formatJobTitle(job, userRole);
        
        events.push({
          id: `job-${job.id}`,
          title: title,
          start: job.date,
          allDay: true,
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          textColor: '#ffffff',
          display: 'block',
          extendedProps: {
            type: eventType,
            job_id: job.id,
            job: job
          }
        });
      });
    } catch (error) {
      console.error('Error fetching employer job postings:', error);
    }
  }
  
  return events;
};

// Get availability for an employee (real API)
export const getAvailability = async (employeeId) => {
  // GET /api/employee-availability/?employee_id={id}
  try {
    const availability = await apiClient.get(`/employee-availability/?employee_id=${employeeId}`);
    const availabilityArray = Array.isArray(availability) ? availability : [];
    
    // Return only available dates (is_available=true)
    return availabilityArray
      .filter(avail => avail.is_available === true)
      .map(avail => avail.date);
  } catch (error) {
    console.error('Error fetching availability:', error);
    return [];
  }
};

// Mark availability (employee) - real API
export const markAvailability = async (employeeId, date) => {
  // POST /api/employee-availability/
  // Body: { employee: employeeId, date: date, is_available: true }
  try {
    const response = await apiClient.post('/employee-availability/', {
      user: employeeId,
      date: date,
      is_available: true
    });
    
    return {
      id: response.id || `avail-${Date.now()}`,
      title: 'Available',
      start: date,
      allDay: true,
      backgroundColor: '#20b2aa',
      borderColor: '#008b8b',
      type: 'availability'
    };
  } catch (error) {
    // If availability already exists, the backend might return an error
    // Try to update it instead
    console.error('Error marking availability:', error);
    throw error;
  }
};

// Remove availability (employee) - real API
export const removeAvailability = async (employeeId, date) => {
  // First, find the availability record
  try {
    const availability = await apiClient.get(`/employee-availability/?employee_id=${employeeId}&date=${date}`);
    const availabilityArray = Array.isArray(availability) ? availability : [];
    
    if (availabilityArray.length > 0) {
      const availRecord = availabilityArray[0];
      // DELETE /api/employee-availability/{id}/
      await apiClient.delete(`/employee-availability/${availRecord.id}/`);
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error removing availability:', error);
    throw error;
  }
};

// Get events for a specific date range (real API)
export const getEventsByDateRange = async (userId, userRole, startDate, endDate) => {
  const allEvents = await getCalendarEvents(userId, userRole);
  
  return allEvents.filter(event => {
    const eventDate = new Date(event.start);
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    return eventDate >= start && eventDate <= end;
  });
};

// Get job details for calendar event (real API)
export const getEventJobDetails = async (eventId, eventType) => {
  // Extract ID from event ID format (e.g., "job-123" or "match-456")
  const parts = eventId.split('-');
  if (parts.length < 2) return null;
  
  const id = parseInt(parts[1]);
  if (isNaN(id)) return null;
  
  if (eventType === 'posted_job' || eventType === 'accepted_job' || eventType === 'completed_job') {
    // Fetch job posting
    try {
      return await apiClient.get(`/job-postings/${id}/`);
    } catch (error) {
      console.error('Error fetching job details:', error);
      return null;
    }
  }
  
  if (eventType === 'potential_match') {
    // Fetch match details
    try {
      const match = await apiClient.get(`/job-matches/${id}/`);
      return {
        ...(match.job_posting_detail || match.job_posting),
        match: match
      };
    } catch (error) {
      console.error('Error fetching match details:', error);
      return null;
    }
  }
  
  return null;
};

